import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useRef, useState } from "react";
import { Navigate, Outlet, Link } from "react-router-dom";
import { API_BASE } from "../config/apiBase";
import Loading from "../pages/Loading";
import "../styles/pages/auth/_protected-route.scss";

// ─── Icons ────────────────────────────────────────────────────────────────────
const IconWarn = () => (
    <svg
        width="22"
        height="22"
        viewBox="0 0 22 22"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M11 2L20.5 19H1.5L11 2z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
        />
        <path
            d="M11 9v5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
        />
        <circle cx="11" cy="16.5" r="0.75" fill="currentColor" />
    </svg>
);

const IconRefresh = () => (
    <svg
        width="15"
        height="15"
        viewBox="0 0 15 15"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M1.5 7.5A6 6 0 0 1 13 4M13.5 7.5A6 6 0 0 1 2 11"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
        />
        <path
            d="M10.5 4h3V1"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M4.5 11H1.5v3"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const IconMail = () => (
    <svg
        width="15"
        height="15"
        viewBox="0 0 15 15"
        fill="none"
        aria-hidden="true"
    >
        <rect
            x="1.5"
            y="3"
            width="12"
            height="9"
            rx="1.2"
            stroke="currentColor"
            strokeWidth="1.3"
        />
        <path
            d="M1.5 5l6 4 6-4"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
        />
    </svg>
);

const IconChevron = () => (
    <svg
        width="13"
        height="13"
        viewBox="0 0 13 13"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M4 5l2.5 2.5L9 5"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// ─── FetchError ───────────────────────────────────────────────────────────────
function FetchError({ syncError }) {
    const message =
        typeof syncError === "string"
            ? syncError
            : syncError?.message
              ? syncError.message
              : JSON.stringify(syncError, null, 2);

    const mailBody = encodeURIComponent(
        `Hi Mooz support,\n\nI'm seeing this error while preparing my account:\n\n${message}\n\nThanks!`
    );

    return (
        <section className="pr-error" role="alert" aria-live="polite">
            <div className="pr-error__bg" aria-hidden="true">
                <div className="pr-error__bg-orb pr-error__bg-orb--1" />
                <div className="pr-error__bg-orb pr-error__bg-orb--2" />
            </div>
            <div className="pr-error__card">
                <div className="pr-error__icon-wrap" aria-hidden="true">
                    <IconWarn />
                </div>
                <div className="pr-error__heading-group">
                    <p className="pr-error__eyebrow">Setup issue</p>
                    <h2 className="pr-error__title">
                        We couldn't prepare your account
                    </h2>
                    <p className="pr-error__body">
                        This is usually temporary. Refresh the page to try
                        again. If it keeps happening, send the details below to
                        our support team.
                    </p>
                </div>
                <details className="pr-error__details">
                    <summary className="pr-error__summary">
                        <IconChevron /> View error details
                    </summary>
                    <pre className="pr-error__code">
                        {message || "No error details were provided."}
                    </pre>
                </details>
                <div className="pr-error__actions">
                    <button
                        type="button"
                        className="pr-error__btn pr-error__btn--primary"
                        onClick={() => window.location.reload()}
                    >
                        <IconRefresh /> Try again
                    </button>
                    <Link
                        className="pr-error__btn pr-error__btn--secondary"
                        to="/contact"
                    >
                        <IconMail /> Email support
                    </Link>
                </div>
            </div>
        </section>
    );
}

function SuspendedGate({ email, onLogout }) {
    const mailBody = encodeURIComponent(
        `Hi Mooz support,\n\nMy account appears to be suspended.\n\nAccount email: ${email || "(unknown)"}\n\nPlease help me restore access.\n\nThanks!`
    );
    return (
        <section className="pr-error" role="alert" aria-live="polite">
            <div className="pr-error__bg" aria-hidden="true">
                <div className="pr-error__bg-orb pr-error__bg-orb--1" />
                <div className="pr-error__bg-orb pr-error__bg-orb--2" />
            </div>
            <div className="pr-error__card">
                <div className="pr-error__icon-wrap" aria-hidden="true">
                    <IconWarn />
                </div>
                <div className="pr-error__heading-group">
                    <p className="pr-error__eyebrow">Account suspended</p>
                    <h2 className="pr-error__title">You have been suspended</h2>
                    <p className="pr-error__body">
                        Your account is currently suspended, so you can’t access
                        the dashboard. If you think this is a mistake, contact
                        support.
                    </p>
                </div>
                <div className="pr-error__actions">
                    <Link
                        className="pr-error__btn pr-error__btn--secondary"
                        to="/contact"
                    >
                        <IconMail /> Email support
                    </Link>
                    <button
                        type="button"
                        className="pr-error__btn pr-error__btn--primary"
                        onClick={onLogout}
                    >
                        Log out
                    </button>
                    <a
                        className="pr-error__btn pr-error__btn--secondary"
                        href="/"
                    >
                        Go to home
                    </a>
                </div>
            </div>
        </section>
    );
}

// ─── ProtectedRoute ───────────────────────────────────────────────────────────
export default function ProtectedRoute() {
    const {
        isAuthenticated,
        isLoading,
        loginWithRedirect,
        getAccessTokenSilently,
        logout,
        user,
    } = useAuth0();

    const [synced, setSynced] = useState(false);
    const [syncError, setSyncError] = useState(null);
    const [isSuspended, setIsSuspended] = useState(false);
    const didSync = useRef(false);
    const lastSub = useRef(null);

    useEffect(() => {
        if (isLoading) return;
        if (user?.sub && lastSub.current && user.sub !== lastSub.current) {
            didSync.current = false;
            setSynced(false);
            setSyncError(null);
            setIsSuspended(false);
        }
        if (user?.sub) lastSub.current = user.sub;
    }, [isLoading, user?.sub]);

    useEffect(() => {
        if (isLoading) return;
        if (!isAuthenticated) {
            loginWithRedirect();
            return;
        }
        if (didSync.current) return;

        (async () => {
            try {
                const token = await getAccessTokenSilently({
                    authorizationParams: {
                        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                    },
                });
                const res = await fetch(`${API_BASE}/auth/sync`, {
                    method: "POST",
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) {
                    const text = await res.text();
                    throw new Error(text || "Sync failed");
                }
                try {
                    const data = await res.json();
                    if (data?.user && typeof window !== "undefined") {
                        window.dispatchEvent(
                            new CustomEvent("mooz:profileUpdated", {
                                detail: data.user,
                            })
                        );
                    }
                    if (data?.user?.status === "suspended") {
                        setIsSuspended(true);
                    }
                } catch {
                    /* noop */
                }
                didSync.current = true;
                setSynced(true);
            } catch (e) {
                const raw = e?.message || "";
                const friendly = raw.includes("Failed to fetch")
                    ? "Can't reach the server. Please check your connection and try again."
                    : raw;
                setSyncError(friendly);
            }
        })();
    }, [isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently]);

    if (isLoading) return <Loading message="Preparing your dashboard…" />;
    if (syncError) return <FetchError syncError={syncError} />;
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    if (!synced) return <Loading message="Preparing your dashboard…" />;

    if (isSuspended) {
        return (
            <SuspendedGate
                email={user?.email}
                onLogout={() =>
                    logout({
                        logoutParams: {
                            returnTo: window.location.origin,
                        },
                    })
                }
            />
        );
    }

    const needsVerification = user?.email_verified === false;

    if (!needsVerification) return <Outlet />;

    return (
        <div className="pr-verify-wrap is-blocked">
            <div className="pr-verify-content" aria-hidden="true">
                <Outlet />
            </div>
            <div className="pr-verify" role="dialog" aria-modal="true">
                <div className="pr-verify__card">
                    <p className="pr-verify__eyebrow">Verify your email</p>
                    <h2 className="pr-verify__title">
                        Check your inbox to continue
                    </h2>
                    <p className="pr-verify__body">
                        We sent a verification link to{" "}
                        <strong>{user?.email || "your email"}</strong>. Please
                        verify your email to unlock the dashboard.
                    </p>
                    <div className="pr-verify__actions">
                        <button
                            type="button"
                            className="pr-verify__btn pr-verify__btn--primary"
                            onClick={() => window.location.reload()}
                        >
                            I've verified
                        </button>
                        <button
                            type="button"
                            className="pr-verify__btn pr-verify__btn--ghost"
                            onClick={() =>
                                logout({
                                    logoutParams: {
                                        returnTo: window.location.origin,
                                    },
                                })
                            }
                        >
                            Log out
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
